import { TransformOptions } from '@supabase/storage-js'

import { signedUrlCache } from './signed-url-cache'

import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'

export const calculatePaginationRange = (page: number, itemsPerPage: number) => {
  const start = (page - 1) * itemsPerPage
  const end = page * itemsPerPage - 1
  return { start, end }
}

// ================================================
// SIGNED URLS & CACHING
// ================================================

// TODO: remove slowly
export const getAssetPublicUrl = ({
  bucket,
  assetName,
  opts,
}: {
  bucket: Bucket
  assetName: string
  opts?: { download?: string | boolean; transform?: TransformOptions }
}) => {
  return supabase.storage.from(bucket).getPublicUrl(assetName, opts).data.publicUrl
}

export type Bucket = 'user_profile_pictures' | 'technician_photos' | 'service_photos' | 'order_attachments'

export const getAssetSignedUrl = async ({
  bucket,
  assetName,
  expiresIn = 60 * 60 * 24, // 24 hours
}: {
  bucket: Bucket
  assetName: string
  expiresIn?: number
}) => {
  const cachedUrl = signedUrlCache.get(assetName)
  if (cachedUrl) {
    return cachedUrl
  }

  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(assetName, expiresIn)

  if (error) {
    logger.error('Failed to create signed URL:', { errorMessage: error, bucket, assetName })
    return null
  }

  // Cache the URL
  signedUrlCache.set(assetName, data.signedUrl, expiresIn)
  return data.signedUrl
}

export const getSignedUrls = async (
  paths: string[],
  bucket: Bucket,
  expiresIn: number = 60 * 60 * 24, // 24 hours
  options: {
    forceCacheBust?: boolean
    version?: number
  } = {},
): Promise<string[]> => {
  if (paths.length === 0) return []

  const { forceCacheBust = false, version = 1 } = options

  const cachedUrls: string[] = []
  const pathsToRequest: string[] = []

  // Check cache for each path
  for (const path of paths) {
    if (forceCacheBust) {
      pathsToRequest.push(path)
    } else {
      const cachedUrl = signedUrlCache.get(path, version)
      if (cachedUrl) {
        cachedUrls.push(cachedUrl)
      } else {
        pathsToRequest.push(path)
      }
    }
  }

  // If all URLs are cached, return them
  if (pathsToRequest.length === 0) {
    return cachedUrls
  }

  // Fetch new signed URLs
  const { data, error } = await supabase.storage.from(bucket).createSignedUrls(pathsToRequest, expiresIn)

  if (error) {
    logger.error('Failed to create signed URLs:', {
      errorMessage: error,
      bucket,
      paths: pathsToRequest,
    })
    throw error
  }

  const signedUrls = [...cachedUrls]
  const errors: Array<{ path: string | null; error: string }> = []

  for (const record of data) {
    if (record.error || !record.signedUrl || !record.path) {
      errors.push({
        path: record.path,
        error: record.error || 'Missing signed URL or path',
      })
    } else {
      let finalUrl = record.signedUrl

      // Add cache busting parameter if needed
      if (forceCacheBust) {
        const url = new URL(finalUrl)
        url.searchParams.set('t', Date.now().toString())
        finalUrl = url.toString()
      }

      signedUrls.push(finalUrl)

      // Cache the URL (without cache busting param for consistency)
      signedUrlCache.set(record.path, record.signedUrl, expiresIn, version)
    }
  }

  if (errors.length > 0) {
    logger.warn('Some signed URLs failed to generate:', {
      errors,
      bucket,
    })
  }

  return signedUrls
}
